import React from "react";

// ============================================================
// CustomIcon — gold-foil iconography for Kingdom Connect.
//
// Usage:
//   <CustomIcon name="calendar" size={20} />
//   <CustomIcon name="dashboard" size={24} title="Home" />
//
// Renders an <img> from /assets/custom-icons/icon-<name>.png.
// All 64 registered names are listed in ICON_REGISTRY below —
// any other value logs a console warning (dev only) and falls
// back to nothing (so we don't break the UI mid-rollout).
//
// The component returns an inline-block <img> so it can be
// placed inside <span> / <button> wrappers without triggering
// React's validateDOMNesting warnings.
// ============================================================

export const ICON_REGISTRY = Object.freeze({
  // navigation / structure
  "dashboard":         "icon-church-home.png",   // 🏠 → use church-home for the church-home dashboard
  "home":              "icon-church-home.png",   // alias
  "browse":            "icon-compass.png",       // 🧭
  "hamburger":         "icon-hamburger-menu.png",// ☰
  "back":              "icon-back-arrow.png",    // ←
  "arrow-right":       "icon-back-arrow.png",    // → (uses back-arrow flipped via CSS at call sites, or as decorative chevron)
  "close":             "icon-close-x.png",       // ✕ / ❌
  "search":            "icon-search.png",        // 🔍
  "logout":            "icon-logout.png",        // 🚪
  "add":               "icon-add-circle.png",    // ➕
  "plus":              "icon-add-circle.png",    // alias for add (➕)

  // notifications & status
  "bell":              "icon-bell-notification.png", // 🔔
  "check":             "icon-checkmark-circle.png",  // ✅ / ✓
  "warning":           "icon-warning-triangle.png",  // ⚠ / 🚫
  "trash":             "icon-trash-delete.png",      // 🗑

  // people / profile
  "people":            "icon-people-group.png",      // 👥
  "people-group":      "icon-people-group.png",      // alias for group / team contexts
  "person":            "icon-person-profile.png",    // 👤
  "person-settings":   "icon-person-settings.png",   // 🛡 (admin/users)
  "celebrate":         "icon-celebrate-person.png",  // 🙋
  "chat":              "icon-chat-people.png",       // 🤝
  "care-heart":        "icon-care-heart.png",        // 💝

  // family / church
  "family":            "icon-family-home.png",       // 🏡
  "church":            "icon-church-home.png",       // ⛪ / 🏛
  "crown":             "icon-crown.png",             // 👑
  "dove":              "icon-dove-cross.png",        // 🕊
  "praying-hands":     "icon-praying-hands.png",     // 🙏 / 🤲

  // calendar / time
  "calendar":          "icon-calendar.png",          // 📅
  "calendar-clock":    "icon-calendar-clock.png",    // 📆 / 🗓
  "calendar-highlight":"icon-calendar-highlight.png",// (date picker accent)
  "clock":             "icon-clock.png",             // 🕒
  "day-night":         "icon-day-night.png",         // 🌙 / ☀

  // events / celebrations
  "event-ticket":      "icon-event-ticket.png",      // 🎟
  "birthday-cake":     "icon-birthday-cake.png",     // 🎂
  "party":             "icon-party-popper.png",      // 🎉
  "gift":              "icon-gift.png",              // 🎁
  "wedding":           "icon-wedding-rings.png",     // 💍
  "baby":              "icon-baby-dedication.png",   // 👶
  "baptism":           "icon-baptism-water.png",     // 💧
  "bible":             "icon-bible.png",             // 📖

  // tools / admin
  "tools":             "icon-tools.png",             // 🛠 / ⚙
  "settings":          "icon-tools.png",             // alias for settings/config contexts
  "lock":              "icon-lock.png",              // 🔒
  "bolt":              "icon-lightning-bolt.png",    // ⚡
  "qr":                "icon-qr-code.png",           // (qr posters)
  "printer":           "icon-printer.png",           // 🖨
  "edit":              "icon-edit-pencil.png",       // ✎ / ✏
  "document-edit":     "icon-document-edit.png",     // 📜
  "clipboard":         "icon-clipboard-checklist.png",// 📋
  "folder":            "icon-folder.png",            // 📁 / 📦
  "download-inbox":    "icon-download-inbox.png",    // 📥
  "megaphone":         "icon-megaphone.png",         // 📢

  // analytics / awards
  "chart":             "icon-analytics-chart.png",   // 📈 / 📊
  "trophy":            "icon-trophy.png",            // 🏆

  // money / donations / finance
  "money":             "icon-donations-money.png",   // 💰 / 💵
  "coin":              "icon-donations-money.png",   // Finance module — coin logo (reuses money PNG)
  "expense":           "icon-clipboard-checklist.png",// Finance module — expense record
  "document":          "icon-document-edit.png",     // 📄 (fallback for print/document actions)

  // donations subtypes
  "food":              "icon-food.png",              // 🍞
  "tshirt":            "icon-tshirt.png",            // 👕
  "armchair":          "icon-armchair.png",          // 🛋

  // misc
  "eye-open":          "icon-eye-open.png",          // 👁
  "eye-closed":        "icon-eye-closed.png",        // 🙈
  "camera-upload":     "icon-camera-upload.png",     // 📷
  "picture":           "icon-camera-upload.png",     // alias — image/photo contexts
  "upload":            "icon-camera-upload.png",     // alias — generic upload buttons
  "phone":             "icon-phone.png",             // 📞
  "link":              "icon-link.png",              // 🔗
  "empty-mailbox":     "icon-empty-mailbox.png",     // 📭
  "paint-palette":     "icon-paint-palette.png",     // 🎨
  "tag":               "icon-tag.png",               // 🏷
  "recurring":         "icon-recurring.png",         // 🔁
  "sparkle":           "icon-sparkle.png",           // ✨
  "app-mobile":        "icon-app-mobile.png",        // 📱
  "globe":             "icon-global-location.png",   // 🌍
});

const ICON_BASE_PATH = "/assets/custom-icons/";

export function CustomIcon({
  name,
  size = 20,
  title,
  className = "",
  style = {},
  ...rest
}) {
  const file = ICON_REGISTRY[name];
  if (!file) {
    if (typeof console !== "undefined" && process.env.NODE_ENV !== "production") {
      console.warn(`[CustomIcon] Unknown icon name: "${name}"`);
    }
    return null;
  }
  // The `arrow-right` alias reuses the `icon-back-arrow.png` asset flipped
  // horizontally — we only ship one arrow PNG in the 64-icon set.
  const needsFlip = name === "arrow-right";
  return (
    <img
      src={`${ICON_BASE_PATH}${file}`}
      alt={title || ""}
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : "true"}
      data-icon={name}
      className={`kc-custom-icon ${className}`}
      width={size}
      height={size}
      style={{
        display: "inline-block",
        width: size,
        height: size,
        objectFit: "contain",
        verticalAlign: "middle",
        flexShrink: 0,
        ...(needsFlip ? { transform: "scaleX(-1)" } : null),
        ...style,
      }}
      draggable={false}
      {...rest}
    />
  );
}

export default CustomIcon;
